import * as _ from 'lodash';
import * as React from 'react';
import Highlighter from 'react-highlight-words';
import { Alert, Button, Card, Input } from 'reactstrap';
import { ContentCardHeader } from '../../../components/ui/ContentCardHeader';
import { DataGrid, IColumn } from '../../../components/ui/DataGrid';
import { FontAwesomeIcon } from '../../../components/ui/FontAwesomeIcon';
import { GetAllSurveyReviews, ISurveyReview, ISurveyReviewOptions, DeleteSurveys, IMessage, PublishSurveys, PublishSelectedSurveys } from './utils';
import { Redirect } from 'react-router';
import { userData } from 'util/userData';

// tslint:disable-next-line: no-empty-interface
interface ISurveyReviewProps extends ISurveyReviewOptions {
  completedMessage: string
}

interface ISurveyReviewGridState {
  surveyReviews: ISurveyReview[];
  filteredSurveyReviews: ISurveyReview[];
  searchStr: string;
  searchInputVal: string;
  selectedSurveyReviews: ISurveyReview[];
  message: IMessage;
  working: boolean;
  loading: boolean
  publishWorking: boolean
  errorMessage?: string
  publishSelectedWorking?: boolean
}

export class SurveyReviewGrid extends React.Component<ISurveyReviewProps, ISurveyReviewGridState> {
  static SurveyReviewGridProcessWorkKey = 'SurveyReviewGridProcessWorkKey'

  state: ISurveyReviewGridState = {
    surveyReviews: [],
    filteredSurveyReviews: [],
    selectedSurveyReviews: [],
    searchStr: '',
    searchInputVal: '',
    message: {},
    working: false,
    loading: false,
    publishWorking: false,
    publishSelectedWorking: false,
  }

  updateSearchStrDebounce = _.debounce((searchStr: string) => {
    this.setState({ searchStr });

    this.updateFilteredSurveyReviews(searchStr);
  }, 1000);

  constructor(props) {
    super(props)
    this.setMessage = this.setMessage.bind(this)
  }

  componentWillMount() {
    this.setState({ loading: true })
    GetAllSurveyReviews(this.props)
      .then((surveyReviews) => this.setState({ surveyReviews, filteredSurveyReviews: surveyReviews, loading: false }))
  }

  setSearchInputVal(searchInputVal: string) {
    this.setState({ searchInputVal }, () => {
      this.updateSearchStrDebounce(searchInputVal);
    })
  }

  updateFilteredSurveyReviews(searchStr = this.state.searchStr) {
    const { surveyReviews } = this.state;
    let filteredSurveyReviews: ISurveyReview[] = [];

    if (!searchStr) {
      filteredSurveyReviews = surveyReviews;
    } else {
      const searchKeys: Array<keyof ISurveyReview> = ['survey', 'branchName', 'customerPhone', 'customerName', 'client', 'employeeName'];
      const searchStrLower = searchStr.toLowerCase();

      filteredSurveyReviews = surveyReviews.filter((record) => {
        return searchKeys.some((searchKey) => {
          const searchValueLower = String(record[searchKey]).toLowerCase();

          return searchValueLower.indexOf(searchStrLower) >= 0;
        })
      })
    }

    this.setState({ filteredSurveyReviews });
  }

  isSelected(record: ISurveyReview) {
    const { selectedSurveyReviews } = this.state
    const selectedIndex = selectedSurveyReviews.findIndex((selectedSurveyReview) => selectedSurveyReview === record)

    return selectedIndex >= 0
  }

  toggleSelected(record: ISurveyReview) {
    const { selectedSurveyReviews } = this.state
    const selectedIndex = selectedSurveyReviews.findIndex((selectedSurveyReview) => selectedSurveyReview === record)

    if (selectedIndex >= 0) {
      selectedSurveyReviews.splice(selectedIndex, 1)
    } else {
      selectedSurveyReviews.push(record)
    }

    this.setState({ selectedSurveyReviews })
  }

  renderBulkCheck(record: ISurveyReview) {
    const disableActions = false
    const isSelected = this.isSelected(record)
    const isNotSelected = !isSelected
    const icon = isSelected ? 'check-square' : 'square'
    const className = (isNotSelected || disableActions) ? 'text-dark' : ''
    const iconElement = (
      <FontAwesomeIcon className={className}
        verticalAlign="initial"
        icon={icon}
        regular={isNotSelected && !disableActions} />
    )

    if (disableActions) {
      return (<span>{iconElement}</span>)
    }

    return (
      <a href="javascript:void(0)" onClick={() => this.toggleSelected(record)}>
        {iconElement}
      </a>
    )
  }

  highlightColumn(column: IColumn<ISurveyReview>): IColumn<ISurveyReview> {
    const { property = '' } = column;

    column.render = (record) => (
      <Highlighter
        highlightClassName="highlight"
        searchWords={[this.state.searchStr]}
        textToHighlight={String(record[property])}
      />
    )

    return column;
  }

  getColumns() {
    const { newSurveys = false } = this.props;

    const columns: Array<IColumn<ISurveyReview>> = [
      this.highlightColumn({ property: 'externalDataReference', header: 'ID', width: 100 }),
      {
        property: 'surveyDate', header: 'Survey Date', width: 100, render: (record) =>
          <span>{record.surveyDate.format('MM/DD/YYYY')}</span>
      },
      this.highlightColumn({ property: 'survey', header: 'Survey', width: 150 }),
      this.highlightColumn({ property: 'branchName', header: 'Branch Name', width: 150 }),
      this.highlightColumn({ property: 'customerPhone', header: 'Customer Phone', width: 100 }),
      this.highlightColumn({ property: 'customerName', header: 'Customer Name' }),
      //this.highlightColumn({ property: 'client', header: 'Client', width: 175 }),
      this.highlightColumn({ property: 'employeeName', header: 'Employee Name' }),
    ]

    if (newSurveys) {
      const bulkColumn: IColumn<ISurveyReview> = { header: 'Bulk', render: (record) => this.renderBulkCheck(record) }

      columns.splice(0, 0, bulkColumn)
    }

    return columns;
  }

  onBulkEdit() {
    const { selectedSurveyReviews } = this.state;

    window.localStorage.setItem(SurveyReviewGrid.SurveyReviewGridProcessWorkKey, JSON.stringify(selectedSurveyReviews))
    window.location.href = 'process_comments'
  }

  setMessage(message: IMessage) {
    this.setState({ message })
  }

  onBulkDelete() {
    const { selectedSurveyReviews } = this.state;

    this.setState({ working: true })

    DeleteSurveys(selectedSurveyReviews)
      .then((message) => {
        this.setState({ selectedSurveyReviews: [], message }, () => {
          setTimeout(function () {
            this.setMessage({ message: '' })
          }.bind(this), 5000)
        })
      })
      .then(() => GetAllSurveyReviews(this.props)
        .then((surveyReviews) => this.setState({ surveyReviews, filteredSurveyReviews: surveyReviews, working: false })))
  }

  onPublish() {
    this.setState({ publishWorking: true },
      () => PublishSurveys().then(() => {
        GetAllSurveyReviews(this.props)
          .then((surveyReviews) => this.setState({ surveyReviews, filteredSurveyReviews: surveyReviews, working: false, publishWorking: false }))
      })
    )
  }

  onPublishSelected() {
    const { selectedSurveyReviews } = this.state

    this.setState({ publishSelectedWorking: true })

    PublishSelectedSurveys(selectedSurveyReviews)
      .then((message) => {
        GetAllSurveyReviews(this.props).then((surveyReviews) => this.setState({ selectedSurveyReviews: [], message, surveyReviews, filteredSurveyReviews: surveyReviews, working: false, publishSelectedWorking: false }))
      })
      .catch((err) => {
        this.setState({ errorMessage: err, publishSelectedWorking: false }, () => {
          setTimeout(() => {
            this.setState({ errorMessage: '' })
          }, 5000)
        });
      })
  }

  onProcessNewComments() {
    const { surveyReviews } = this.state

    window.localStorage.setItem(SurveyReviewGrid.SurveyReviewGridProcessWorkKey, JSON.stringify(surveyReviews))
    window.location.href = 'process_comments'
  }

  renderMessage() {
    const { message } = this.state
    return (
      <Alert color={'info'} >
        Message: {message.message
        }
      </Alert >
    )
  }

  render() {
    const { completedSurveys, deletedSurveys, newSurveys } = this.props
    const { surveyReviews, selectedSurveyReviews, filteredSurveyReviews, working, loading, publishWorking } = this.state
    const selectedSurveyReviewsLength = selectedSurveyReviews.length
    const selectedSurveyReviewsLengthMoreThanZero = selectedSurveyReviewsLength > 0
    let type = ''

    if (completedSurveys) {
      type = 'Completed'
    } else if (deletedSurveys) {
      type = 'Deleted'
    } else if (newSurveys) {
      type = 'New'
    }

    return (
      <Card>
        <ContentCardHeader sticky borderBottom
          header={
            <section>
              <section className="d-md-flex justify-content-between">
                <header>
                  <h2 className="d-inline-block mr-2">
                    <span>{type}&nbsp;Surveys</span>
                    {newSurveys && selectedSurveyReviewsLengthMoreThanZero &&
                      <small className="text-muted">
                        <span className="text-monospace">&nbsp;{selectedSurveyReviewsLength.toLocaleString()}</span>
                        <span>&nbsp;Selected</span>
                      </small>
                    }
                    {loading &&
                      <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin muted />
                    }
                    {working &&
                      <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin />
                    }
                  </h2>
                </header>
                <article>
                  <Input type="text"
                    placeholder="Search"
                    onChange={(e) => this.setSearchInputVal(e.currentTarget.value)}
                    value={this.state.searchInputVal}
                  />
                </article>
              </section>
              {newSurveys &&
                <section className="d-md-flex justify-content-between">
                  <article>
                    <Button color="action" className="mr-2"
                      disabled={!selectedSurveyReviewsLengthMoreThanZero}
                      onClick={() => this.onBulkEdit()}>
                      <FontAwesomeIcon icon="edit" verticalAlign="middle" fixedWidth size="md" />
                      <span>&nbsp;Bulk Edit</span>
                    </Button>
                    <Button color="action" className="mr-2"
                      disabled={!selectedSurveyReviewsLengthMoreThanZero}
                      onClick={() => this.onPublishSelected()}>
                      <FontAwesomeIcon regular icon="newspaper" verticalAlign="middle" fixedWidth size="md" />
                      <span>&nbsp;Publish Selected</span>{(this.state.publishSelectedWorking) ? <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin /> : null}
                    </Button>
                    <Button color="destructive"
                      disabled={!selectedSurveyReviewsLengthMoreThanZero}
                      onClick={() => this.onBulkDelete()}>
                      <FontAwesomeIcon icon="trash" verticalAlign="middle" fixedWidth size="md" />
                      <span>&nbsp;Bulk Delete</span>
                    </Button>
                  </article>
                  <article>
                    <Button color="primary" className="mr-2"
                      disabled={publishWorking}
                      onClick={() => this.onPublish()}>
                      <span>Publish All Surveys</span>{
                        (publishWorking) ? <FontAwesomeIcon icon="cog" size="sm" verticalAlign="middle" spin /> : null
                      }
                    </Button>
                    <Button color="primary"
                      onClick={() => this.onProcessNewComments()}>
                      <span>Process New Comments</span>
                    </Button>
                  </article>
                </section>
              }
            </section>
          }
        />
        {this.state.message.message && this.state.message.message != '' &&
          this.renderMessage()
        }
        {filteredSurveyReviews.length > 0 &&
          <DataGrid<ISurveyReview>
            records={filteredSurveyReviews}
            columns={this.getColumns()}
          />
        }
        {
          surveyReviews.length === 0 && !working &&
          < Alert color={'success'}>
            {this.props.completedMessage}
          </Alert>
        }
      </Card>
    )
  }
}
